import { createDraftSafeSelector } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { CountryType } from '../../models/countryUnitActuals';
import { filterBusinessUnits } from './utils';
import { selectUserCountries, selectUserRole } from '../userData/selectors';
import { AD } from '../../components/RoleRestricted/utils';
import { selectCurrentUser } from '../users/selectors';

export const countriesSelector = (state: RootState) => {
  const uniqueCountries = new Map();

  state.selectData.businessUnits.forEach((bu) => {
    uniqueCountries.set(bu.countryId, {
      countryId: bu.countryId,
      countryName: bu.countryName,
    });
  });

  return Array.from(uniqueCountries.values()).sort((a, b) =>
    a.countryName.localeCompare(b.countryName),
  ) as CountryType[];
};

export const countryOptions = createDraftSafeSelector(
  countriesSelector,
  (countries) =>
    countries.map((country) => ({
      value: country.countryId,
      label: country.countryName,
    })),
);

export const getSelectedCountryCodes = (state: RootState) =>
  state.selectData.selectedCountryCodes;

export const selectBUsRaw = (state: RootState) =>
  state.selectData.businessUnits;

export const selectedDataLoadingSelector = (state: RootState) =>
  state.selectData.loading;
export const selectedBUs = (state: RootState) => state.selectData.selectedBUs;

export const selectedBUsState = createDraftSafeSelector(
  selectedBUs,
  (selectedBUs) => selectedBUs,
);

export const selectBUs = createDraftSafeSelector(selectBUsRaw, (bus) =>
  bus?.map((bu) => ({
    ...bu,
    businessUnitFullName: `${bu.businessUnitId} - ${bu.businessUnitFullName}`,
  })),
);

export const selectFilteredCountries = createDraftSafeSelector(
  selectBUs,
  (bus) => {
    const seen = new Set();

    return bus.filter((bu) => {
      const duplicate = seen.has(bu?.countryId);
      seen.add(bu?.countryId);
      return !duplicate;
    });
  },
);

export const selectCountriesByUserPermissions = createDraftSafeSelector(
  selectUserRole,
  selectUserCountries,
  selectFilteredCountries,
  selectCurrentUser,
  (role, userCountries, countries, user) => {
    const allCountries = { countryId: 'All', countryName: 'All' };

    if (role === AD && user?.countries?.includes('All')) {
      return [];
    } else if (role === AD) {
      const countryMap = new Map(countries.map((c) => [c.countryId, c]));
      return userCountries
        .map((countryId) => countryMap.get(countryId))
        .filter(Boolean);
    }

    return [allCountries, ...countries];
  },
);

export const selectFinancialFrameCountryOptions = createDraftSafeSelector(
  selectUserRole,
  selectUserCountries,
  countriesSelector,
  (role, userCountries, countries) => {
    const accessibleCountries =
      role === AD && !userCountries?.includes('All')
        ? countries.filter((country) =>
            userCountries?.includes(country.countryId),
          )
        : countries;

    return accessibleCountries.map((country) => ({
      value: country.countryId,
      label: country.countryName,
    }));
  },
);

export const getBusinessUnits = createDraftSafeSelector(
  [selectBUs],
  (businessUnits) => filterBusinessUnits(businessUnits),
);

export const selectFilteredClusters = createDraftSafeSelector(
  selectBUs,
  (bus) => {
    const arr = [];
    bus?.map((bu) => {
      if (!arr.find((entry) => entry === bu.clusterName))
        arr.push(bu.clusterName);
    });
    return arr;
  },
);

export const selectFilteredBus = (countries, allowedCountries) =>
  createDraftSafeSelector(selectBUs, (bus) => {
    const rangeCountries = countries?.includes('All')
      ? allowedCountries
      : countries;
    const countriesArray = rangeCountries?.map((country) => country?.countryId);
    return bus.filter((bu) => countriesArray?.includes(bu?.countryId));
  });
